/*
 * ping-sample-web-react-todo-oidc
 *
 * route.js
 *
 * Copyright (c) 2026 Ping Identity Corporation. All rights reserved.
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */

import React, { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { DEBUGGER } from '../constants';
import Loading from '../components/utilities/loading';
import { OidcContext } from '../context/oidc.context';

/**
 * @function useAuthValidation - Custom hook for validating user authentication
 * @param {boolean} auth - client state on whether user is authenticated
 * @param {function} setAuth - global state method for setting user authentication status
 * @param {Object} oidcClient - OIDC client used for user/token validation
 * @returns {Array}
 */
function useAuthValidation(auth, setAuth, oidcClient) {
  /**
   * React state "hook"
   *
   * This has three possible states: 'unknown', 'valid' and 'invalid'.
   */
  const [isValid, setValid] = useState('unknown');

  useEffect(() => {
    async function validateAccessToken() {
      /**
       * First, check to see if the user has recently been authenticated
       */
      if (auth) {
        if (!oidcClient) {
          return;
        }
        /**
         * If we they have been authenticated, validate that assumption
         */
        try {
          /** *****************************************************************
           * SDK INTEGRATION POINT
           * Summary: Optional client-side route access validation
           * ------------------------------------------------------------------
           * Details: Here, you could just make sure tokens exist –
           * TokenStorage.get() – or, validate tokens, renew expiry timers,
           * session checks ... Below, we are calling the userinfo endpoint to
           * ensure valid tokens before continuing, but it's optional.
           ***************************************************************** */
          if (DEBUGGER) debugger;
          const user = await oidcClient.user.info();
          if ('error' in user) {
            console.info(`Info: route validation; ${user.error}`);
            setAuth(false);
            setValid('invalid');
            return;
          }
          setValid('valid');
        } catch (err) {
          console.info(`Info: route validation; ${err}`);

          setAuth(false);
          setValid('invalid');
        }
      } else {
        /**
         * If we have no record of their authenticated, no need to call the server
         */
        setValid('invalid');
      }
    }

    validateAccessToken();
    // Only `auth` is mutable, all others, even `setAuth` are "stable"
  }, [auth, oidcClient]);

  return [
    {
      isValid,
    },
  ];
}

/**
 * @function ProtectedRoute - This function extends the ReactRouter component to
 * protect routes from unauthenticated access.
 * Inspired by: https://ui.dev/react-router-v5-protected-routes-authentication/
 * @param {Object} props - React props
 * @param {Object} props.children - React components passed as children
 * @param string[] path - React-Router path prop
 * @returns {Object} - Wrapped React Router component
 */
export function ProtectedRoute({ children }) {
  // Get "global" state from Context API
  const [{ isAuthenticated, oidcClient }, { setAuthentication }] = useContext(OidcContext);
  // Custom hook for validating user's access token
  const [{ isValid }] = useAuthValidation(isAuthenticated, setAuthentication, oidcClient);

  switch (isValid) {
    case 'valid':
      // Access token has been confirmed to be valid
      return children;
    case 'invalid':
      // Access token has been confirmed to be invalid
      return <Navigate to="/login" />;
    default:
      // State is 'unknown', so we are waiting on token validation
      return <Loading classes="pt-5" message="Verifying access ... " />;
  }
}
